import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { TcComponentStandardInformation } from "../../../../tc-models/tc-req-dto/input-tc-component-details.res_dto";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";

interface ComponentStandardInfoProps {
  componentStandardInfoData: TcComponentStandardInformation[];
  setComponentStandardInfoData: React.Dispatch<
    React.SetStateAction<TcComponentStandardInformation[]>
  >;
}

// Define Yup validation schema for each row
const validationSchema = Yup.object()
  .shape({
    // inputTcComponentStandardInformationOrder: Yup.string().required("Order is required"),
    inputTcComponentStandardLabelGrade: Yup.string().nullable(),
    inputTcComponentCertifiedWeight: Yup.number()
      .typeError("Component certified weight must be a valid number")
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .test(
        "max-decimals",
        "Number must be a valid decimal number with upto 2 decimal places.",
        (value) => value == null || /^\d+(\.\d{1,2})?$/.test(value.toString())
      ),
    inputTcComponentCertifiedWeightUsed: Yup.number()
      .typeError("Component certified weight used must be a valid number")
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .test(
        "max-decimals",
        "Number must have up to 2 decimal places.",
        (value) => value == null || /^\d+(\.\d{1,2})?$/.test(value.toString())
      ),

    // inputTcComponentCertifiedWeightUsed: Yup.string().notRequired()
    //   .test(
    //     "is-exactly-two-decimal-places",
    //     "Number must be a valid decimal number with upto 2 decimal places.",
    //     (value: string | undefined | null) => {
    //       if (!value|| value===undefined || value == null || value === '') return true; // Allow empty values for required validation to handle
    //       return /^(0|[0-9]\d*|0\.\d{1,2}|[0-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
    //     }
    //   )
    //   .typeError("Component certified weight used must be a valid number"),
  })
  .test("at-least-one", "At least one field is required", function (values) {
    return values.inputTcComponentStandardLabelGrade ||
      values.inputTcComponentCertifiedWeight ||
      values.inputTcComponentCertifiedWeightUsed
      ? true
      : this.createError({ message: "At least one field is required" });
  });

const ComponentStandardInfo: React.FC<ComponentStandardInfoProps> = ({
  componentStandardInfoData,
  setComponentStandardInfoData,
}) => {
  const [data, setData] = useState<TcComponentStandardInformation[]>(
    componentStandardInfoData
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<TcComponentStandardInformation[]>(
    componentStandardInfoData
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [labelGrades, setLabelGrades] = useState([]);
  const { isRestrictPopUpSave, setRestrictPopUpSave ,getLabelGrades } = useTransactionCertificateStore();

  useEffect(() => {
    setLabelGrades(getLabelGrades);
  }, []);
  const formik = useFormik<TcComponentStandardInformation>({
    initialValues: {
      inputTcComponentStandardInformationOrder: "",
      inputTcComponentStandardLabelGrade: "",
      inputTcComponentCertifiedWeight: null,
      inputTcComponentCertifiedWeightUsed: null,
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: TcComponentStandardInformation = {
      inputTcComponentStandardInformationOrder: "",
      inputTcComponentStandardLabelGrade: "",
      inputTcComponentCertifiedWeight: null,
      inputTcComponentCertifiedWeightUsed: null,
    };
    setRestrictPopUpSave(true);
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setData(tableData);
    setRestrictPopUpSave(false);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };
  const { updateField } = useFormActions("input-tc-component-details");

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;
    if (
      (formik.values?.inputTcComponentCertifiedWeight === null &&
        formik.values?.inputTcComponentCertifiedWeightUsed === null &&
        formik.values.inputTcComponentStandardLabelGrade == "") ||
      (formik.values?.inputTcComponentCertifiedWeight &&
        formik.values?.inputTcComponentCertifiedWeight <= 0) ||
      (formik.values?.inputTcComponentCertifiedWeightUsed &&
        formik.values?.inputTcComponentCertifiedWeightUsed <= 0)
    ) {
      return;
    }
    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setTableData(newData);
    setRestrictPopUpSave(false);
    setComponentStandardInfoData(newData); // Update parent with current data
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const columns: EditableColumnDef<TcComponentStandardInformation>[] = [
    {
      accessorKey: "inputTcComponentStandardLabelGrade",
      minSize: 60,
      maxSize: 60,
      header: "Standard Label Grade",
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="inputTcComponentStandardLabelGrade"
          value={value}
          options={labelGrades}
          enableSearch={true}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ),
    },
    {
      accessorKey: "inputTcComponentCertifiedWeight",
      minSize: 30,
      maxSize: 60,
      header: "Certified Weight (kg)",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcComponentCertifiedWeight"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            // Only allow numbers with max 2 decimal places
            const regex = /^\d*\.?\d{0,2}$/;
            if (regex.test(newValue) || newValue === '') {
              onChange(newValue);
            }
            // If regex doesn't match, the value won't be updated
          }}
          placeholder="Enter certified weight"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "inputTcComponentCertifiedWeightUsed",
      minSize: 30,
      maxSize: 60,
      header: "Weight Used (kg)",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcComponentCertifiedWeightUsed"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            // Only allow numbers with max 2 decimal places
            const regex = /^\d*\.?\d{0,2}$/;
            if (regex.test(newValue) || newValue === '') {
              onChange(newValue);
            }
            // If regex doesn't match, the value won't be updated
          }}
          placeholder="Enter weight used"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  useEffect(() => {
    data.forEach((item: any, index) => {
      item.tcProductRawMaterialOrder = index + 1;
    });
    updateField("product-component-standard-info", data);
  }, [data]);

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
    setRestrictPopUpSave(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setComponentStandardInfoData(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          border: "2px solid rgba(128, 128, 128, .2)",
          my: 4,
        }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton onClick={handleAddNewRow} disabled={isRestrictPopUpSave} sx={{ mb: 2 }}>
            Add New Row
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default ComponentStandardInfo;
